import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, List, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useNutritionManagement } from '../hooks/useNutritionManagement'

const { Title, Text } = Typography
const { Option } = Select

const NutritionManagement = ({ nutrition, setNutrition }) => {
  const [form] = Form.useForm()
  const [selectedNutrition, setSelectedNutrition] = useState(null)
  const { nutritionOptions, isLoading } = useNutritionManagement()

  useEffect(() => {
    form.resetFields()
  }, [selectedNutrition, form])

  const handleAddNutrition = (values) => {
    const newNutrition = {
      id: Date.now(),
      name: values.name,
      type: values.type,
      description: values.description,
      restrictions: values.restrictions,
      goals: values.goals,
      frequency: values.frequency
    }

    setNutrition([...nutrition, newNutrition])
    form.resetFields()
  }

  const handleDeleteNutrition = (id) => {
    setNutrition(nutrition.filter((item) => item.id !== id))
    if (selectedNutrition && selectedNutrition.id === id) {
      setSelectedNutrition(null)
    }
  }

  const handleEditNutrition = () => {
    if (!selectedNutrition) return

    const values = form.getFieldsValue()
    const updatedNutrition = nutrition.map((item) => {
      if (item.id === selectedNutrition.id) {
        return {
          ...item,
          name: values.name,
          type: values.type,
          description: values.description,
          restrictions: values.restrictions,
          goals: values.goals,
          frequency: values.frequency
        }
      }
      return item
    })

    setNutrition(updatedNutrition)
    setSelectedNutrition(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Nutrition & Dietary Planning</Title>
        <Text>Configure dietary plans, nutritional guidance, and meal planning for the wellness plan.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Nutrition Plan Items' loading={isLoading}>
          <List
            dataSource={nutrition}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteNutrition(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.type} - ${item.frequency}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedNutrition(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedNutrition(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Nutrition Plan
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedNutrition ? 'Edit Nutrition Plan' : 'Add Nutrition Plan'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedNutrition ? handleEditNutrition : handleAddNutrition}
            initialValues={selectedNutrition || {}}
          >
            <Form.Item name='name' label='Plan Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Mediterranean Diet Plan' />
            </Form.Item>

            <Form.Item name='type' label='Diet Type' rules={[{ required: true, message: 'Please select a diet type' }]}>
              <Select placeholder='Select diet type'>
                {nutritionOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={3} placeholder='Describe the nutrition plan...' />
            </Form.Item>

            <Form.Item name='restrictions' label='Dietary Restrictions'>
              <Select mode='multiple' placeholder='Select any dietary restrictions'>
                <Option value='gluten-free'>Gluten-Free</Option>
                <Option value='dairy-free'>Dairy-Free</Option>
                <Option value='nut-free'>Nut-Free</Option>
                <Option value='vegan'>Vegan</Option>
                <Option value='vegetarian'>Vegetarian</Option>
                <Option value='low-sodium'>Low Sodium</Option>
                <Option value='low-sugar'>Low Sugar</Option>
              </Select>
            </Form.Item>

            <Form.Item name='goals' label='Nutritional Goals'>
              <Select mode='multiple' placeholder='Select nutrition goals'>
                <Option value='weight-management'>Weight Management</Option>
                <Option value='increase-energy'>Increase Energy</Option>
                <Option value='improve-digestion'>Improve Digestion</Option>
                <Option value='lower-cholesterol'>Lower Cholesterol</Option>
                <Option value='manage-blood-sugar'>Manage Blood Sugar</Option>
                <Option value='hydration'>Improve Hydration</Option>
                <Option value='balanced-nutrition'>Balanced Nutrition</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='frequency'
              label='Frequency'
              rules={[{ required: true, message: 'Please select a frequency' }]}
            >
              <Select placeholder='Select frequency'>
                <Option value='daily'>Daily</Option>
                <Option value='weekly'>Weekly</Option>
                <Option value='monthly'>Monthly</Option>
                <Option value='as-needed'>As Needed</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedNutrition ? 'Update' : 'Add'} Nutrition Plan
                </Button>
                {selectedNutrition && <Button onClick={() => setSelectedNutrition(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default NutritionManagement
